import { useState, useEffect } from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  Autocomplete,
} from "@/components/atoms";
import { Typography, IconButton } from "@mui/material";
import { LoadingState, ErrorState } from "@/components/views";
import { isValidEmail } from "@/utils/validators";
import InstagramIcon from "@mui/icons-material/Instagram";
import XIcon from "@mui/icons-material/X";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { AutocompleteOption } from "@/components/atoms/Autocomplete";
import { useContactForm } from "@/hooks";

export const CONTACT_SUBJECT_OPTIONS = [
  { key: 0, value: "Select a subject" },
  ...(["General Inquiry", "Support", "Feedback", "Other"] as const).map(
    (value, index) => ({
      key: index + 1,
      value,
    }),
  ),
] as const;

export type ContactSubject = (typeof CONTACT_SUBJECT_OPTIONS)[number]["value"];

export const ContactPage = () => {
  const { submitContactForm, loading, error, success } = useContactForm();

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState<AutocompleteOption>(
    CONTACT_SUBJECT_OPTIONS[0],
  );

  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    setEmailError(
      email && !isValidEmail(email) ? "Invalid email address" : null,
    );
  }, [email]);

  useEffect(() => {
    setIsFormValid(
      !emailError && email !== "" && message !== "" && subject.value !== "",
    );
  }, [emailError, email, message, subject]);

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();

    const payload = {
      email,
      first_name: firstName,
      last_name: lastName,
      subject: subject.value as ContactSubject,
      message,
    };

    await submitContactForm(payload);
    if (success) {
      alert("Email sent successfully!");
      setEmail("");
      setFirstName("");
      setLastName("");
      setMessage("");
      setSubject(CONTACT_SUBJECT_OPTIONS[0]);
    }
  };

  return (
    <Container
      componentName="contact"
      sx={{
        flexDirection: "column",
      }}
    >
      <Box componentName="contact-parent" display="flex" sx={{ margin: 2 }}>
        {error ? (
          <ErrorState errors={error} />
        ) : loading ? (
          <LoadingState />
        ) : (
          <Box
            componentName="contact-page"
            border={false}
            sx={{
              display: "flex",
              flexDirection: "column",
              p: 4,
              my: 2,
              mx: 10,
            }}
          >
            <Typography
              variant="h3"
              align="center"
              color="primary.dark"
              sx={{
                my: 1,
                lineHeight: 2,
                textTransform: "capitalize",
                letterSpacing: 2,
                wordSpacing: 2,
              }}
            >
              Contact Me
            </Typography>
            <Box
              componentName="contact-upper"
              border={false}
              sx={{
                padding: 2,
                marginTop: 2,
                // flex: 1,
                display: "flex",
                flexDirection: "column",
                // width: "100%",
              }}
            >
              {success && (
                <Typography
                  variant="body1"
                  color="success.main"
                  align="center"
                  sx={{ mb: 2 }}
                >
                  Message sent successfully!
                </Typography>
              )}
              <Box
                component="form"
                componentName="contact-form"
                onSubmit={handleSubmit}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  // width: "100%",
                }}
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  componentName="name-row"
                  columnGap={2}
                  sx={{ display: "flex", flexDirection: "row" }}
                  border={false}
                >
                  <TextField
                    componentName="contact-first-name"
                    placeholder="First Name"
                    variant="outlined"
                    value={firstName}
                    label="First Name"
                    onChange={e => setFirstName(e.target.value)}
                    required
                    fullWidth
                  />
                  <TextField
                    componentName="contact-last-name"
                    placeholder="Last Name"
                    variant="outlined"
                    value={lastName}
                    label="Last Name"
                    onChange={e => setLastName(e.target.value)}
                    required
                    fullWidth
                  />
                </Box>
                <TextField
                  componentName="contact-email"
                  placeholder="Email"
                  variant="outlined"
                  value={email}
                  label="Email"
                  onChange={e => setEmail(e.target.value)}
                  required
                  error={!!emailError}
                  helperText={emailError}
                  fullWidth
                />
                <Autocomplete
                  required
                  componentName="contact"
                  freeSolo={false}
                  options={CONTACT_SUBJECT_OPTIONS}
                  value={subject}
                  inputLabel="Message Subject"
                  onChange={(_, newValue) => {
                    if (
                      !newValue ||
                      Array.isArray(newValue) ||
                      typeof newValue === "string"
                    ) {
                      setSubject(CONTACT_SUBJECT_OPTIONS[0]);
                    } else {
                      setSubject(newValue as AutocompleteOption);
                    }
                  }}
                  boxSx={{ mb: 2, mt: 0 }}
                  autocompleteSx={{ mb: 2, mt: 0 }}
                  getOptionDisabled={option => option.key === 0}
                />

                <TextField
                  componentName="contact-message"
                  placeholder="Message"
                  variant="outlined"
                  value={message}
                  label="Message"
                  onChange={e => setMessage(e.target.value)}
                  required
                  multiline
                  rows={4}
                  fullWidth
                />

                <Button
                  componentName="submit-contact"
                  color="primary"
                  type="submit"
                  disabled={!isFormValid || loading}
                  fullWidth
                >
                  Submit
                </Button>
              </Box>
            </Box>

            {/* Social Media Links Section */}
            <Box
              componentName="contact-lower"
              sx={{
                padding: 2,
                marginTop: 4,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography variant="h4" gutterBottom align="center">
                Socials
              </Typography>
              <Box
                componentName="social-media-links"
                border={false}
                sx={{ display: "flex", gap: 2, mt: 2 }}
              >
                <IconButton
                  data-testid="instagram-button"
                  color="primary"
                  href="https://www.instagram.com/pharmdsinit/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <InstagramIcon fontSize="large" />
                </IconButton>
                <IconButton
                  data-testid="twitter-button"
                  color="primary"
                  href="https://x.com/pharmdsinit"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <XIcon fontSize="large" />
                </IconButton>
                <IconButton
                  data-testid="linkedin-button"
                  color="primary"
                  href="https://www.linkedin.com/company/pharmds-in-it/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LinkedInIcon fontSize="large" />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};
